import { getScriptKind } from '@cli-utils/ts-morph.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { ts } from 'ts-morph'

export function namespaceGeneratedIdentifiers(
  content: string,
  namespace: string,
  filePath: string
): string {
  const resolved = resolveAdminNamespace(namespace)
  const typeReplacements = new Map([
    ['UserData', 'User'],
    ['UsersResponse', 'UsersPage'],
    ['SettingData', 'Setting'],
    ['UpsertSettingInput', 'SettingUpsertInput'],
    ['UpsertSettingResult', 'SettingUpsertResult'],
    ['AdminNavigationItem', `${resolved.pascal}NavigationItem`],
    ['AdminUserSummary', `${resolved.pascal}UserSummary`],
    ['AdminMedia', `${resolved.pascal}Media`]
  ])
  const valueReplacements = new Map([['adminMedia', `${resolved.camel}Media`]])
  const sourceFile = ts.createSourceFile(
    filePath,
    content,
    ts.ScriptTarget.Latest,
    true,
    getScriptKind(filePath)
  )
  const edits: Array<{ start: number; end: number; value: string }> = []

  function addEdit(node: ts.Identifier, value: string): void {
    edits.push({
      start: node.getStart(sourceFile),
      end: node.getEnd(),
      value
    })
  }

  function getModulePathForSpecifier(node: ts.Node): string | null {
    if (ts.isImportSpecifier(node)) {
      const namedBindings = node.parent
      const importClause = namedBindings.parent
      const importDeclaration = importClause.parent
      if (ts.isImportDeclaration(importDeclaration)) {
        return getModuleSpecifierText(importDeclaration.moduleSpecifier)
      }
    }

    if (ts.isExportSpecifier(node)) {
      const namedExports = node.parent
      const exportDeclaration = namedExports.parent
      if (ts.isExportDeclaration(exportDeclaration) && exportDeclaration.moduleSpecifier) {
        return getModuleSpecifierText(exportDeclaration.moduleSpecifier)
      }
    }

    return null
  }

  function getModuleSpecifierText(moduleSpecifier: ts.Expression): string | null {
    if (
      ts.isStringLiteral(moduleSpecifier) ||
      ts.isNoSubstitutionTemplateLiteral(moduleSpecifier)
    ) {
      return moduleSpecifier.text
    }

    return null
  }

  function isGeneratedAdminModulePath(modulePath: string): boolean {
    return (
      modulePath === '@admin' ||
      modulePath.startsWith('@admin/') ||
      modulePath === resolved.alias ||
      modulePath.startsWith(`${resolved.alias}/`)
    )
  }

  function isGeneratedMediaTableModulePath(modulePath: string): boolean {
    return (
      modulePath === '@admin/db/schema' ||
      modulePath === `${resolved.alias}/db/schema` ||
      modulePath.endsWith('/db/schema')
    )
  }

  function isImportOrExportSpecifierName(node: ts.Identifier): boolean {
    const parent = node.parent
    return (
      (ts.isImportSpecifier(parent) && (parent.name === node || parent.propertyName === node)) ||
      (ts.isExportSpecifier(parent) && (parent.name === node || parent.propertyName === node))
    )
  }

  function shouldRewriteSpecifier(
    node: ts.Identifier,
    replacements: Map<string, string>,
    modulePathPredicate: (modulePath: string) => boolean
  ): string | null {
    if (!isImportOrExportSpecifierName(node)) {
      return null
    }

    const parent = node.parent as ts.ImportSpecifier | ts.ExportSpecifier
    const modulePath = getModulePathForSpecifier(parent)
    if (!modulePath || !modulePathPredicate(modulePath)) {
      return null
    }

    return replacements.get(node.text) ?? null
  }

  function shouldRewriteTypeIdentifier(node: ts.Identifier): string | null {
    const specifierReplacement = shouldRewriteSpecifier(
      node,
      typeReplacements,
      isGeneratedAdminModulePath
    )
    if (specifierReplacement) {
      return specifierReplacement
    }

    const replacement = typeReplacements.get(node.text)
    if (!replacement) {
      return null
    }

    const parent = node.parent
    if (
      (ts.isTypeReferenceNode(parent) && parent.typeName === node) ||
      (ts.isExpressionWithTypeArguments(parent) && parent.expression === node) ||
      (ts.isInterfaceDeclaration(parent) && parent.name === node) ||
      (ts.isTypeAliasDeclaration(parent) && parent.name === node)
    ) {
      return replacement
    }

    return null
  }

  function isValueDeclarationOrPropertyName(node: ts.Identifier): boolean {
    const parent = node.parent

    return (
      (ts.isImportSpecifier(parent) && (parent.name === node || parent.propertyName === node)) ||
      (ts.isExportSpecifier(parent) && (parent.name === node || parent.propertyName === node)) ||
      (ts.isPropertyAccessExpression(parent) && parent.name === node) ||
      (ts.isPropertyAssignment(parent) && parent.name === node) ||
      (ts.isShorthandPropertyAssignment(parent) && parent.name === node) ||
      (ts.isBindingElement(parent) && parent.name === node) ||
      (ts.isVariableDeclaration(parent) && parent.name === node) ||
      (ts.isParameter(parent) && parent.name === node) ||
      (ts.isFunctionDeclaration(parent) && parent.name === node) ||
      (ts.isFunctionExpression(parent) && parent.name === node) ||
      (ts.isPropertyDeclaration(parent) && parent.name === node) ||
      (ts.isMethodDeclaration(parent) && parent.name === node) ||
      (ts.isMethodSignature(parent) && parent.name === node) ||
      (ts.isPropertySignature(parent) && parent.name === node)
    )
  }

  function shouldRewriteValueIdentifier(node: ts.Identifier): string | null {
    const specifierReplacement = shouldRewriteSpecifier(
      node,
      valueReplacements,
      isGeneratedMediaTableModulePath
    )
    if (specifierReplacement) {
      return specifierReplacement
    }

    const replacement = valueReplacements.get(node.text)
    if (!replacement || isValueDeclarationOrPropertyName(node)) {
      return null
    }

    return replacement
  }

  function visit(node: ts.Node): void {
    if (ts.isIdentifier(node)) {
      const value = shouldRewriteTypeIdentifier(node) ?? shouldRewriteValueIdentifier(node)
      if (value) {
        addEdit(node, value)
      }
    }

    ts.forEachChild(node, visit)
  }

  visit(sourceFile)

  if (edits.length === 0) {
    return content
  }

  let output = content
  for (const edit of edits.slice().reverse()) {
    output = `${output.slice(0, edit.start)}${edit.value}${output.slice(edit.end)}`
  }

  return output
}
